'use client';

import { useEffect, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import { Navigation, Thumbs, Autoplay, EffectFade } from 'swiper/modules';
// @ts-ignore
import 'swiper/css';
// @ts-ignore
import 'swiper/css/navigation';
// @ts-ignore
import 'swiper/css/thumbs';
// @ts-ignore
import 'swiper/css/effect-fade';
import Button from '../../ui/Button';
import Image from 'next/image';
import { ArrowIcon } from '../../common/ArrowIcon';
import { homePageData } from '../../utils/page-data/homePage';
import HeroAnimation from './HeroAnimation';

const AUTOPLAY_DELAY = 6000;

export default function HeroSwiper() {
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const mainSwiperRef = useRef<SwiperType | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const progressRef = useRef(0);
  const lastTimeRef = useRef<number | null>(null);

  const totalSlides = homePageData?.slides.length ?? 0;

  const isFirstSlide = activeIndex === 0;
  const isLastSlide = activeIndex === totalSlides - 1;

  const isPausedRef = useRef(false);

  useEffect(() => {
    progressRef.current = 0;
    lastTimeRef.current = null;

    const animate = (time: number) => {
      if (!isPausedRef.current) {
        const lastTime = lastTimeRef.current ?? time;
        const delta = time - lastTime;

        progressRef.current += (delta / AUTOPLAY_DELAY) * 100;

        if (progressRef.current >= 100) {
          progressRef.current = 100;

          if (activeIndex === totalSlides - 1) {
            mainSwiperRef.current?.slideTo(0);
          } else {
            mainSwiperRef.current?.slideNext();
          }
          return;
        }
      }

      lastTimeRef.current = time;
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current)
        cancelAnimationFrame(animationFrameRef.current);
    };
  }, [activeIndex, totalSlides]);
  return (
    <div
      onMouseEnter={() => {
        isPausedRef.current = true;
        setIsPaused(true);
      }}
      onMouseLeave={() => {
        isPausedRef.current = false;
        lastTimeRef.current = performance.now();
        setIsPaused(false);
      }}
      className="mb-5 overflow-hidden bg-heroBg bg-cover bg-center bg-no-repeat md:mx-[30px] md:rounded-3xl lg:bg-full"
    >
      <div className="flex items-center justify-center overflow-hidden pb-10 lg:pb-0">
        <div className="relative flex h-full w-full flex-col">
          <Swiper
            modules={[Navigation, Thumbs, EffectFade]}
            effect="fade"
            fadeEffect={{ crossFade: true }}
            speed={700}
            thumbs={{ swiper: thumbsSwiper }}
            onSwiper={(swiper) => {
              mainSwiperRef.current = swiper;
            }}
            onSlideChange={(swiper) => {
              setActiveIndex(swiper.activeIndex);
            }}
            className="relative z-0 mb-5 h-full w-full md:mb-0"
          >
            {homePageData?.slides.map((slide) => (
              <SwiperSlide key={slide.tab}>
                <div className="h-full w-full p-5 xl:p-12">
                  {slide.type === 'animated' && (
                    <HeroAnimation isActive={activeIndex === 0} />
                  )}
                  {slide.type !== 'animated' && (
                    <Image
                      src={slide.bg}
                      alt={slide.heading}
                      width={1080}
                      height={900}
                      className="absolute left-1/2 top-1/2 z-10 hidden -translate-x-1/2 -translate-y-1/2 object-cover object-center [-webkit-mask-composite:destination-in] [-webkit-mask-image:linear-gradient(to_right,transparent_0%,black_15%,black_85%,transparent_100%),linear-gradient(to_bottom,transparent_0%,black_15%,black_85%,transparent_100%)] [mask-composite:intersect] [mask-image:linear-gradient(to_right,transparent_0%,black_15%,black_85%,transparent_100%),linear-gradient(to_bottom,transparent_0%,black_15%,black_85%,transparent_100%)] lg:block lg:h-full"
                    />
                  )}
                  <div className="relative left-0 top-0 z-40 mb-5 grid h-max w-full gap-5 lg:mb-0 lg:min-h-[500px] lg:grid-cols-2 lg:grid-rows-2 xl:min-h-[690px] xl:gap-10">
                    <div className="relative z-20 h-max max-w-[700px]">
                      <h1 className="text-4xl font-bold text-primary xl:text-5xl xl:leading-[56px]">
                        {slide.heading}
                      </h1>
                      <div className="mt-5 flex flex-col gap-4 lg:hidden">
                        <p className="text-base font-normal text-optDesc">
                          {slide.description}
                        </p>
                        <div className="flex flex-wrap items-center gap-4">
                          {slide.primaryCta && (
                            <Button
                              btnName={slide.primaryCta.label}
                              redirectionLink={slide.primaryCta.href}
                              isBackgroung={true}
                            />
                          )}
                          {slide.secondaryCta && (
                            <Button
                              btnName={slide.secondaryCta.label}
                              redirectionLink={slide.secondaryCta.href}
                            />
                          )}
                        </div>
                      </div>
                    </div>

                    {slide.partnerBadges && (
                      <div className="hidden w-full justify-end gap-3 lg:flex">
                        {slide.partnerBadges.map((src, i) => (
                          <div
                            key={i}
                            className="flex max-h-[80px] w-full max-w-[200px] items-center justify-center rounded-lg bg-white p-3 shadow-md"
                          >
                            <Image
                              src={src}
                              alt="Partner badge"
                              width={150}
                              height={50}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="hidden w-full flex-col items-end gap-3 lg:flex">
                      {slide.stats && (
                        <div className="flex w-max flex-col gap-3">
                          {slide.stats.map((stat) => (
                            <div
                              key={stat.label}
                              className="flex flex-col items-start rounded-xl bg-white px-6 py-3 text-right shadow-md xl:gap-2"
                            >
                              <h2 className="text-xl font-bold text-slate-900 xl:text-2xl">
                                {stat.value}
                              </h2>
                              <p className="text-sm text-slate-500">
                                {stat.label}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="relative z-20 ml-auto mt-auto hidden max-w-[550px] before:pointer-events-none before:-inset-[80px] before:-z-10 before:bg-[#f8f9ffa4] before:content-[''] before:[-webkit-mask-composite:destination-in] before:[-webkit-mask-image:linear-gradient(to_right,transparent_0%,black_10%,black_90%,transparent_100%),linear-gradient(to_bottom,transparent_0%,black_20%,black_80%,transparent_100%)] before:[mask-composite:intersect] before:[mask-image:linear-gradient(to_right,transparent_0%,black_10%,black_90%,transparent_100%),linear-gradient(to_bottom,transparent_0%,black_20%,black_80%,transparent_100%)] lg:col-start-2 lg:row-start-2 lg:block lg:before:absolute 4xl:max-w-[650px]">
                      <div className="flex flex-col gap-4">
                        <p className="text-base font-normal text-optDesc">
                          {slide.description}
                        </p>
                        <div className="flex flex-wrap items-center gap-4">
                          {slide.primaryCta && (
                            <Button
                              btnName={slide.primaryCta.label}
                              redirectionLink={slide.primaryCta.href}
                              isBackgroung={true}
                            />
                          )}
                          {slide.secondaryCta && (
                            <Button
                              btnName={slide.secondaryCta.label}
                              redirectionLink={slide.secondaryCta.href}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
          <div
            onMouseEnter={(e) => {
              e.stopPropagation();
              isPausedRef.current = false;
              lastTimeRef.current = performance.now();
              setIsPaused(false); // controls area ma hammesha running
            }}
            onMouseLeave={(e) => {
              e.stopPropagation();
              // haju hero ni andar j chhe, etle pacha pause thavu joiye
              isPausedRef.current = true;
              setIsPaused(true);
            }}
            className="bottom-10 z-20 flex w-full max-w-[470px] flex-col items-start gap-4 pl-5 lg:absolute lg:left-10 lg:pl-0 3xl:max-w-[680px]"
          >
            <div className="flex shrink-0 gap-2">
              <button
                type="button"
                aria-label="Previous slide"
                disabled={isFirstSlide}
                onClick={() => {
                  progressRef.current = 0;
                  mainSwiperRef.current?.slidePrev();
                }}
                className={`flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white shadow-sm transition ${
                  isFirstSlide
                    ? 'cursor-not-allowed opacity-40'
                    : 'hover:bg-slate-50'
                }`}
              >
                <ArrowIcon direction="left" />
              </button>
              <button
                type="button"
                aria-label="Next slide"
                disabled={isLastSlide}
                onClick={() => {
                  progressRef.current = 0;
                  mainSwiperRef.current?.slideNext();
                }}
                className={`flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white shadow-sm transition ${
                  isLastSlide
                    ? 'cursor-not-allowed opacity-40'
                    : 'hover:bg-slate-50'
                }`}
              >
                <ArrowIcon direction="right" />
              </button>
            </div>
            <Swiper
              modules={[Thumbs, Autoplay]}
              onSwiper={setThumbsSwiper}
              slidesPerView={1}
              spaceBetween={8}
              watchSlidesProgress
              breakpoints={{
                768: {
                  slidesPerView: 2,
                },
                1440: {
                  slidesPerView: 3,
                },
              }}
              className="!hidden !h-auto w-full xl:!block"
            >
              {homePageData?.slides.map((slide, i) => {
                const isActive = i === activeIndex;
                return (
                  <SwiperSlide
                    key={slide.tab}
                    className="!h-auto cursor-pointer"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        progressRef.current = 0;
                        mainSwiperRef.current?.slideTo(i);
                      }}
                      className="fle w-full overflow-hidden rounded-xl border border-slate-200 bg-white text-left shadow-sm"
                    >
                      <div className="relative h-[85px] w-full">
                        <Image
                          src={slide.thumb}
                          alt={slide.tab}
                          width={400}
                          height={200}
                          className="h-full w-full object-cover"
                        />
                        <div className="absolute inset-x-0 bottom-0 left-0 h-[3px] w-full">
                          {isActive && (
                            <div
                              key={activeIndex}
                              className="h-full origin-left bg-gradient-to-r from-[#e95025f1] to-[#1a6aa3ed]"
                              style={{
                                animation: `heroProgressFill ${AUTOPLAY_DELAY}ms linear forwards`,
                                animationPlayState: isPaused
                                  ? 'paused'
                                  : 'running',
                              }}
                            />
                          )}
                        </div>
                      </div>
                      <div className="px-3 pb-1.5 pt-1">
                        <span className="text-xs font-medium text-optDesc md:text-sm">
                          {slide.tab}
                        </span>
                      </div>
                    </button>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
        </div>
      </div>
    </div>
  );
}
